import React, { useEffect, useMemo, useState } from 'react';
import { apiClient, posService } from '@/services';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Filter, X, Calendar, Store, CreditCard, ChevronLeft, ChevronRight, RefreshCw, Shield, Download } from 'lucide-react';

type TransactionRow = {
  id: string;
  cuenta_id: string | null;
  pedido_id: string | null;
  tipo: string;
  metodo: string | null;
  monto: number;
  descripcion: string;
  pos_id: number | null;
  user_id: string | null;
  creadaEn: string;
  account?: { id: string; } | null;
  order?: { id: string; } | null;
  payment?: { id: string; estado?: string; metodo?: string } | null;
  user?: { id: string; nombre: string; apellido: string; email: string } | null;
  pos?: { id: number; nombre: string } | null;
};

type PagedResult = {
  transactions: TransactionRow[];
  total: number;
  page: number;
  totalPages: number;
};

type POSOption = { id: number; nombre: string };

type IUserLite = {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  roles: string[];
  activo?: boolean;
};


const TransactionsAdminPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [tipo, setTipo] = useState<string>('');
  const [metodo, setMetodo] = useState<string>('');
  const [posId, setPosId] = useState<string>('');
  const [fechaInicio, setFechaInicio] = useState<string>('');
  const [fechaFin, setFechaFin] = useState<string>('');

  // Options for selects
  const [posList, setPosList] = useState<POSOption[]>([]);

  // Data
  const [data, setData] = useState<PagedResult | null>(null);
  // Independent options for selects (do not shrink with current tipo/metodo filters)
  const [tipoOptions, setTipoOptions] = useState<string[]>([]);
  const [metodoOptions, setMetodoOptions] = useState<string[]>([]);

  // Tabs
  const [activeTab, setActiveTab] = useState<'todas' | 'por-usuario'>('todas');

  // Users list for "por-usuario" tab
  const [users, setUsers] = useState<IUserLite[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [userSearch, setUserSearch] = useState<string>('');
  const rolesOptions = useMemo(() => {
    const set = new Set<string>();
    users.forEach((u) => (u.roles || []).forEach((r) => set.add(r)));
    return Array.from(set).sort();
  }, [users]);
  const filteredUsers = useMemo(() => {
    let list = users;
    if (roleFilter) {
      list = list.filter((u) => (u.roles || []).includes(roleFilter));
    }
    if (userSearch) {
      const term = userSearch.toLowerCase();
      list = list.filter((u) =>
        `${u.nombre} ${u.apellido}`.toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term)
      );
    }
    return list;
  }, [users, roleFilter, userSearch]);

  // Modal state for user transactions
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserLite | null>(null);
  const [userTxData, setUserTxData] = useState<PagedResult | null>(null);
  const [userTxLoading, setUserTxLoading] = useState(false);
  const [userTxError, setUserTxError] = useState<string | null>(null);
  const [userPage, setUserPage] = useState(1);
  const [userLimit, setUserLimit] = useState(25);
  const [userMonth, setUserMonth] = useState<string>(''); // format YYYY-MM
  const userVisibleTotal = useMemo(() => {
    const list = userTxData?.transactions ?? [];
    return list.reduce((sum, tx) => sum + (Number(tx.monto) || 0), 0);
  }, [userTxData?.transactions]);

  const handleDownloadUserTxPDF = async () => {
    const rows = userTxData?.transactions ?? [];
    if (rows.length === 0) return;

    const userName = selectedUser ? `${selectedUser.nombre} ${selectedUser.apellido}` : '';
    const userEmail = selectedUser?.email ?? '';
    const filterText = userMonth ? `Mes: ${userMonth}` : 'Todo';
    const generatedAt = new Date().toLocaleString();
    const totalText = `$${Number(userVisibleTotal).toLocaleString('es-CL')}`;

    const tableBody = [
      [
        { text: 'Fecha', style: 'tableHeader' },
        { text: 'Tipo', style: 'tableHeader' },
        { text: 'Método', style: 'tableHeader' },
        { text: 'Monto', style: 'tableHeader', alignment: 'right' },
        { text: 'POS', style: 'tableHeader' },
        { text: 'Descripción', style: 'tableHeader' },
      ],
      ...rows.map((t) => [
        new Date(t.creadaEn).toLocaleString(),
        t.tipo,
        t.metodo || '-',
        { text: `$${Number(t.monto).toLocaleString('es-CL')}`, alignment: 'right' },
        t.pos?.nombre || (t.pos_id ?? '-'),
        t.descripcion,
      ]),
    ];

    const docDefinition: any = {
      info: {
        title: `Transacciones - ${userName}`,
        author: 'Sistema',
        subject: 'Estado de transacciones por usuario',
      },
      pageSize: 'A4',
      pageMargins: [24, 36, 24, 36],
      content: [
        { text: 'Recibo/Estado de pago', style: 'title' },
        { text: `Generado: ${generatedAt}`, style: 'muted', margin: [0, 2, 0, 10] },
        {
          columns: [
            [
              { text: `Usuario: ${userName} (${userEmail})`, margin: [0, 0, 0, 2] },
              { text: `Filtro: ${filterText}` },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        {
          table: {
            headerRows: 1,
            widths: ['auto', '*', '*', 'auto', '*', '*'],
            body: tableBody,
          },
          layout: 'lightHorizontalLines',
        },
        { text: `Total mostrado: ${totalText}`, style: 'total', margin: [0, 8, 0, 0] },
      ],
      styles: {
        title: { fontSize: 16, bold: true },
        muted: { fontSize: 9, color: '#6b7280' },
        tableHeader: { bold: true, fillColor: '#f3f4f6' },
        total: { bold: true },
      },
    };

    // Cargar pdfMake dinámicamente y generar el PDF
    try {
      const pdfMakeModule = await import('pdfmake/build/pdfmake');
      const pdfFontsModule = await import('pdfmake/build/vfs_fonts');

      const pdfMake = pdfMakeModule.default;
      // El módulo vfs_fonts exporta un objeto con la propiedad pdfMake.vfs
      pdfMake.vfs = (pdfFontsModule as any).pdfMake?.vfs || (pdfFontsModule as any).vfs || pdfFontsModule;

      pdfMake.createPdf(docDefinition).download(`transacciones_${selectedUser?.id || 'usuario'}.pdf`);
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor, intente nuevamente.');
    }
  };

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (tipo) params.set('tipo', tipo);
    if (metodo) params.set('metodo', metodo);
    if (posId) params.set('pos_id', posId);
    if (fechaInicio) params.set('fecha_inicio', fechaInicio);
    if (fechaFin) params.set('fecha_fin', fechaFin);
    return params.toString();
  }, [page, limit, tipo, metodo, posId, fechaInicio, fechaFin]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get<{ success: boolean; data: PagedResult }>(`/api/v1/transactions?${queryParams}`);
      setData(res.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar transacciones');
    } finally {
      setLoading(false);
    }
  };

  // Fetch complete options for tipo/metodo ignoring current tipo/metodo filters
  const fetchFilterOptions = async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '1000');
      if (fechaInicio) params.set('fecha_inicio', fechaInicio);
      if (fechaFin) params.set('fecha_fin', fechaFin);
      // Nota: Intencionalmente NO incluimos 'tipo' ni 'metodo' para no reducir opciones
      const res = await apiClient.get<{ success: boolean; data: PagedResult }>(`/api/v1/transactions?${params.toString()}`);
      const rows = res.data.data?.transactions ?? [];
      const tipoSet = new Set<string>();
      const metodoSet = new Set<string>();
      rows.forEach((t) => {
        if (t.tipo) tipoSet.add(t.tipo);
        if (t.metodo) metodoSet.add(t.metodo);
      });
      setTipoOptions(Array.from(tipoSet).sort());
      setMetodoOptions(Array.from(metodoSet).sort());
    } catch {}
  };

  const fetchPOS = async () => {
    try {
      const list = await posService.getPOSList();
      const active = list.filter((p: any) => p.activo !== false);
      const mapped: POSOption[] = active.map((p: any) => ({ id: p.id, nombre: p.nombre }));
      setPosList(mapped);
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const pageLimit = 1000; // backend allows up to 1000
      let offset = 0;
      let accumulated: any[] = [];
      let total = 0;

      // Fetch in pages until we reach total or get less than pageLimit
      // Avoid infinite loops with a hard cap of 50k users
      const hardCap = 50000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const url = `/api/v1/users?limit=${pageLimit}&offset=${offset}`;
        const res = await apiClient.get<{ success: boolean; data: { users: IUserLite[]; total: number } }>(url);
        const pageUsers = (res.data.data.users || []) as any[];
        total = res.data.data.total || total;
        accumulated = accumulated.concat(pageUsers);

        if (pageUsers.length < pageLimit || accumulated.length >= total || accumulated.length >= hardCap) {
          break;
        }

        offset += pageLimit;
      }

      setUsers(accumulated.map((u) => ({ ...u, roles: Array.isArray(u.roles) ? u.roles : [] })));
    } catch (e: any) {
      setUsersError(e?.response?.data?.message || 'Error al cargar usuarios');
    } finally {
      setUsersLoading(false);
    }
  };

  function getMonthDateRange(monthStr: string): { start?: string; end?: string } {
    if (!monthStr) return {};
    const [yearStr, monStr] = monthStr.split('-');
    const year = Number(yearStr);
    const month = Number(monStr) - 1; // 0-based
    if (Number.isNaN(year) || Number.isNaN(month)) return {};
    const startDate = new Date(Date.UTC(year, month, 1));
    const endDate = new Date(Date.UTC(year, month + 1, 0));
    const start = startDate.toISOString().slice(0, 10);
    const end = endDate.toISOString().slice(0, 10);
    return { start, end };
  }

  const fetchUserTransactions = async (userId: string) => {
    try {
      setUserTxLoading(true);
      setUserTxError(null);
      const params = new URLSearchParams();
      params.set('page', String(userPage));
      params.set('limit', String(userLimit));
      params.set('user_id', userId);
      if (userMonth) {
        const range = getMonthDateRange(userMonth);
        if (range.start) params.set('fecha_inicio', range.start);
        if (range.end) params.set('fecha_fin', range.end);
      }
      const res = await apiClient.get<{ success: boolean; data: PagedResult }>(`/api/v1/transactions?${params.toString()}`);
      setUserTxData(res.data.data);
    } catch (e: any) {
      setUserTxError(e?.response?.data?.message || 'Error al cargar transacciones del usuario');
    } finally {
      setUserTxLoading(false);
    }
  };

  useEffect(() => {
    fetchPOS();
    fetchUsers();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [queryParams]);

  // Update the available options when date range changes
  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaInicio, fechaFin]);

  useEffect(() => {
    if (isUserModalOpen && selectedUser) {
      fetchUserTransactions(selectedUser.id);
    }
  }, [isUserModalOpen, selectedUser, userPage, userLimit, userMonth]);

  const clearFilters = () => {
    setTipo('');
    setMetodo('');
    setPosId('');
    setFechaInicio('');
    setFechaFin('');
    setPage(1);
    setLimit(25);
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const last7Str = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Gestión de Transacciones</h1>
        </div>
        <p className="text-sm text-muted-foreground">Filtra por tipo, método, POS y rango de fechas</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'todas' | 'por-usuario')}>
        <TabsList className="mb-2">
          <TabsTrigger value="todas">Todas</TabsTrigger>
          <TabsTrigger value="por-usuario">Por usuario</TabsTrigger>
        </TabsList>

        <TabsContent value="todas" className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium flex items-center gap-1"><span>Tipo</span></label>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todos</SelectItem>
                    {tipoOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium flex items-center gap-1"><CreditCard className="h-4 w-4" /> <span>Método</span></label>
                <Select value={metodo} onValueChange={setMetodo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todos</SelectItem>
                    {metodoOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium flex items-center gap-1"><Store className="h-4 w-4" /> <span>POS</span></label>
                <Select value={posId} onValueChange={setPosId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todos</SelectItem>
                    {posList.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium flex items-center gap-1"><Calendar className="h-4 w-4" /> <span>Fecha inicio</span></label>
                <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium flex items-center gap-1"><Calendar className="h-4 w-4" /> <span>Fecha fin</span></label>
                <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
              </div>

              <div className="flex items-end gap-2">
                <Button onClick={() => { setPage(1); fetchTransactions(); }} disabled={loading}>
                  {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Aplicar
                </Button>
                <Button variant="secondary" onClick={clearFilters} disabled={loading}>
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              </div>
            </div>

            {(tipo || metodo || posId || fechaInicio || fechaFin) && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground">Filtros activos:</span>
                {tipo && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    Tipo: {tipo}
                    <button onClick={() => setTipo('')} className="ml-1 text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {metodo && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    Método: {metodo}
                    <button onClick={() => setMetodo('')} className="ml-1 text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {posId && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    POS: {posList.find((p) => String(p.id) === posId)?.nombre ?? posId}
                    <button onClick={() => setPosId('')} className="ml-1 text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {fechaInicio && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    Desde: {fechaInicio}
                    <button onClick={() => setFechaInicio('')} className="ml-1 text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {fechaFin && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    Hasta: {fechaFin}
                    <button onClick={() => setFechaFin('')} className="ml-1 text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <Button variant="ghost" className="h-8 px-2" onClick={() => { setFechaInicio(todayStr); setFechaFin(todayStr); }}>
                    Hoy
                  </Button>
                  <Button variant="ghost" className="h-8 px-2" onClick={() => { setFechaInicio(last7Str); setFechaFin(todayStr); }}>
                    Últimos 7 días
                  </Button>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-4 flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                {loading ? 'Cargando…' : error ? error : `${data?.total ?? 0} resultados`}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                </Button>
                <span className="text-sm">Página {data?.page ?? page} de {data?.totalPages ?? 1}</span>
                <Button variant="outline" disabled={(data?.page ?? 1) >= (data?.totalPages ?? 1) || loading} onClick={() => setPage((p) => p + 1)}>
                  Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>POS</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Descripción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-72" /></TableCell>
                    </TableRow>
                  ))}
                  {!loading && (data?.transactions ?? []).map((t) => (
                    <TableRow key={t.id} className="hover:bg-muted/40">
                      <TableCell>{new Date(t.creadaEn).toLocaleString()}</TableCell>
                      <TableCell>{t.tipo}</TableCell>
                      <TableCell>{t.metodo || '-'}</TableCell>
                      <TableCell className="text-right">${Number(t.monto).toLocaleString('es-CL')}</TableCell>
                      <TableCell>{t.pos?.nombre || (t.pos_id ?? '-')}</TableCell>
                      <TableCell>{t.user ? `${t.user.nombre} ${t.user.apellido}` : (t.user_id ?? '-')}</TableCell>
                      <TableCell className="max-w-xs truncate" title={t.descripcion}>{t.descripcion}</TableCell>
                    </TableRow>
                  ))}
                  {!loading && (data?.transactions?.length ?? 0) === 0 && (
                    <TableRow>
                      <TableCell className="p-4 text-center text-sm text-muted-foreground" colSpan={7}>Sin resultados</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="por-usuario" className="space-y-4">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                {usersLoading ? 'Cargando usuarios…' : usersError ? usersError : `${filteredUsers.length} usuarios`}
              </div>
              <div className="flex items-end gap-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium flex items-center gap-1"><span>Buscar</span></label>
                  <Input
                    placeholder="Nombre o email"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium flex items-center gap-1"><Shield className="h-4 w-4" /> <span>Rol</span></label>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      {rolesOptions.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="pb-1">
                  <Button variant="secondary" onClick={() => { setRoleFilter(''); setUserSearch(''); }} disabled={usersLoading || (!roleFilter && !userSearch)}>
                    Limpiar
                  </Button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersLoading && Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`user-skeleton-${i}`}>
                      <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-64" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                    </TableRow>
                  ))}
                  {!usersLoading && filteredUsers.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.nombre} {u.apellido}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => { setSelectedUser(u); setUserPage(1); setUserLimit(25); setUserMonth(''); setIsUserModalOpen(true); }}>
                          Ver compras
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!usersLoading && filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell className="p-4 text-center text-sm text-muted-foreground" colSpan={3}>Sin usuarios</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Dialog open={isUserModalOpen} onOpenChange={(open) => { setIsUserModalOpen(open); if (!open) { setSelectedUser(null); setUserTxData(null); setUserTxError(null); } }}>
            <DialogContent className="max-w-5xl">
              <DialogHeader>
                <DialogTitle>Transacciones de {selectedUser ? `${selectedUser.nombre} ${selectedUser.apellido}` : ''}</DialogTitle>
                <DialogDescription>Filtra por mes para ver las compras del usuario</DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <Card className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <div className="space-y-1">
                      <label className="text-sm font-medium flex items-center gap-1"><Calendar className="h-4 w-4" /> <span>Mes</span></label>
                      <Input type="month" value={userMonth} onChange={(e) => { setUserPage(1); setUserMonth(e.target.value); }} />
                    </div>
                    <div className="sm:col-span-2 flex gap-2">
                      <Button onClick={() => { if (selectedUser) fetchUserTransactions(selectedUser.id); }} disabled={userTxLoading}>
                        {userTxLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Aplicar
                      </Button>
                      <Button variant="secondary" onClick={() => { setUserMonth(''); setUserPage(1); if (selectedUser) fetchUserTransactions(selectedUser.id); }} disabled={userTxLoading}>Limpiar</Button>
                    </div>
                  </div>
                </Card>

                <Card className="p-0 overflow-hidden">
                  <div className="p-4 flex items-center justify-between gap-4">
                    <div className="text-sm text-muted-foreground">
                      {userTxLoading
                        ? 'Cargando…'
                        : userTxError
                        ? userTxError
                        : (
                          <>
                            {`${userTxData?.total ?? 0} resultados`}
                            <span className="ml-2 font-medium text-foreground">
                              {`Total: $${Number(userVisibleTotal).toLocaleString('es-CL')}`}
                            </span>
                          </>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={handleDownloadUserTxPDF}
                        disabled={userTxLoading || ((userTxData?.transactions?.length ?? 0) === 0)}
                      >
                        <Download className="h-4 w-4 mr-1" /> Descargar PDF
                      </Button>
                      <Button variant="outline" disabled={userPage <= 1 || userTxLoading} onClick={() => setUserPage((p) => Math.max(1, p - 1))}>
                        <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                      </Button>
                      <span className="text-sm">Página {userTxData?.page ?? userPage} de {userTxData?.totalPages ?? 1}</span>
                      <Button variant="outline" disabled={(userTxData?.page ?? 1) >= (userTxData?.totalPages ?? 1) || userTxLoading} onClick={() => setUserPage((p) => p + 1)}>
                        Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Método</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                          <TableHead>POS</TableHead>
                          <TableHead>Descripción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userTxLoading && Array.from({ length: 6 }).map((_, i) => (
                          <TableRow key={`user-tx-skeleton-${i}`}>
                            <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                            <TableCell className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-72" /></TableCell>
                          </TableRow>
                        ))}
                        {!userTxLoading && (userTxData?.transactions ?? []).map((t) => (
                          <TableRow key={t.id} className="hover:bg-muted/40">
                            <TableCell>{new Date(t.creadaEn).toLocaleString()}</TableCell>
                            <TableCell>{t.tipo}</TableCell>
                            <TableCell>{t.metodo || '-'}</TableCell>
                            <TableCell className="text-right">${Number(t.monto).toLocaleString('es-CL')}</TableCell>
                            <TableCell>{t.pos?.nombre || (t.pos_id ?? '-')}</TableCell>
                            <TableCell className="max-w-xs truncate" title={t.descripcion}>{t.descripcion}</TableCell>
                          </TableRow>
                        ))}
                        {!userTxLoading && (userTxData?.transactions?.length ?? 0) === 0 && (
                          <TableRow>
                            <TableCell className="p-4 text-center text-sm text-muted-foreground" colSpan={6}>Sin resultados</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TransactionsAdminPage;